import { useQuery } from '@tanstack/react-query';
import type { Capability, Department, Screen } from '@hub/shared';
import { canRead, canWrite, hasCapability } from '@hub/shared';
import { api } from './api.js';

export interface Me {
  user: {
    id: string;
    email: string;
    name: string;
    departments: Department[];
    primaryDepartment: Department;
    /** Značky, na které je účet omezen; `null` = všechny. */
    brands: string[] | null;
  };
  sendMode: 'dry_run' | 'test_allowlist' | 'live';
  sendModeCeiling: 'dry_run' | 'test_allowlist' | 'live';
  clockMode: 'system' | 'simulated';
  now: string;
  defaultTimezone: string;
  appEnv: 'development' | 'test' | 'production';
  build?: {
    commit: string | null;
    committedAt: string | null;
    message: string | null;
    builtAt: string | null;
  };
  /** Aktivní prostředí hubu (D-132); `null` = přepínání není zapnuté. */
  environment: {
    name: 'development' | 'staging' | 'production';
    database: string;
    allowed: Array<'development' | 'staging' | 'production'>;
    canSwitch: boolean;
  } | null;
  /** Obrazovky, které skrývá vypnutá vrstva nasazení (D-250). */
  hiddenScreens?: Screen[];
  /** Administrace bez hesla — jen lokalni vyvoj. */
  authDisabled?: boolean;
}

export function useMe() {
  return useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/me'),
    retry: false,
    refetchInterval: 60_000,
  });
}

export interface Rights {
  departments: Department[];
  can(capability: Capability): boolean;
  read(screen: Screen): boolean;
  write(screen: Screen): boolean;
}

export function rightsFor(departments: Department[]): Rights {
  return {
    departments,
    can: (capability) => hasCapability(departments, capability),
    read: (screen) => canRead(departments, screen),
    write: (screen) => canWrite(departments, screen),
  };
}
